import React, { useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import {
  CIRCLE_OPTIONS,
  MISC_CM_MFL,
  MISC_CM_AG1,
  MISC_CM_OSC,
  MISC_CM_INSIGHT_CHIPS,
  MISC_CM_INSIGHTS,
  MISC_CM_ACTIONS,
  MISC_CM_RECOVERABLE_BANNER,
  MISC_CIRCLE_COLORS,
} from '../../data/miscCmData';
import {
  chartBaseOptions,
  EmptyState,
  ChipStrip,
  InsightCard,
  KpiCard,
  palette,
  PeriodUnavailableState,
  PriorityBadge,
  SectionTitle,
  toneAccent,
  InsightBanner,
} from '../dashboardPrimitives';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const DOMAIN_TABS = [
  { key: 'MFL', label: 'MFL', color: '#3b82f6', bg: '#3b82f616' },
  { key: 'AG1', label: 'AG1', color: '#ec4899', bg: '#ec489916' },
  { key: 'OSC', label: 'OSC', color: '#059669', bg: '#05966916' },
];

const DOMAIN_GRADIENTS = {
  MFL: 'linear-gradient(135deg, #1e3a5f 0%, #3b82f6 100%)',
  AG1: 'linear-gradient(135deg, #831843 0%, #ec4899 100%)',
  OSC: 'linear-gradient(135deg, #064e3b 0%, #059669 100%)',
};

function toneColor(tone) {
  switch (tone) {
    case 'green': return palette.success;
    case 'red': return palette.danger;
    case 'orange': return palette.warning;
    case 'blue': return palette.accent;
    case 'yellow': return palette.warning;
    default: return palette.slateDeep;
  }
}

function kpiPctColor(value) {
  if (value >= 90) return palette.success;
  if (value >= 70) return palette.accent;
  if (value >= 50) return palette.warning;
  return palette.danger;
}

function energyBarColor(value) {
  if (value >= 70) return palette.success;
  if (value >= 50) return palette.warning;
  return palette.danger;
}

function ag1EnergyBarColor(value) {
  if (value >= 70) return palette.success;
  if (value >= 30) return palette.warning;
  return palette.danger;
}

function availBarColor(value) {
  if (value >= 99.95) return palette.success;
  if (value >= 95) return palette.warning;
  if (value >= 90) return palette.orange;
  return palette.danger;
}

function saBarColor(value) {
  if (value >= 80) return palette.success;
  if (value >= 50) return palette.warning;
  if (value >= 30) return palette.orange;
  return palette.danger;
}

function pmTecoBarColor(value) {
  if (value >= 70) return palette.success;
  if (value >= 50) return palette.warning;
  return palette.orange;
}

function visBarColor(value) {
  if (value >= 70) return palette.success;
  if (value >= 55) return palette.warning;
  return palette.orange;
}

function pctCellStyle(value, target) {
  if (value >= target) return { backgroundColor: '#DCFCE7', color: '#166534' };
  if (value >= target * 0.8) return { backgroundColor: '#FEF9C3', color: '#92400E' };
  return { backgroundColor: '#FEE2E2', color: '#991B1B' };
}

/* ──────────────────────────── COMMON BAR OPTIONS ──────────────────────────── */

function barOpts(yMin, yMax, pctTicks = true) {
  return {
    ...chartBaseOptions,
    plugins: {
      ...chartBaseOptions.plugins,
      legend: { display: false },
      tooltip: {
        ...chartBaseOptions.plugins.tooltip,
        callbacks: {
          label: (ctx) => ` ${ctx.raw}${pctTicks ? '%' : ''}`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: { autoSkip: false, color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } },
      },
      y: {
        min: yMin,
        max: yMax,
        grid: { color: palette.border },
        border: { display: false },
        ticks: {
          callback: (v) => pctTicks ? `${v}%` : v,
          color: palette.slateDeep,
          font: { family: 'JetBrains Mono', size: 10, weight: '500' },
        },
      },
    },
  };
}

function groupedBarOpts(yMin, yMax) {
  return {
    ...chartBaseOptions,
    plugins: {
      legend: {
        position: 'top',
        labels: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } },
      },
      tooltip: {
        ...chartBaseOptions.plugins.tooltip,
        callbacks: { label: (ctx) => ` ${ctx.dataset.label}: ${ctx.raw}%` },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: { autoSkip: false, color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } },
      },
      y: {
        min: yMin,
        max: yMax,
        grid: { color: palette.border },
        border: { display: false },
        ticks: {
          callback: (v) => `${v}%`,
          color: palette.slateDeep,
          font: { family: 'JetBrains Mono', size: 10, weight: '500' },
        },
      },
    },
  };
}

function deltaBarOpts() {
  return {
    ...chartBaseOptions,
    plugins: {
      ...chartBaseOptions.plugins,
      legend: { display: false },
      tooltip: {
        ...chartBaseOptions.plugins.tooltip,
        callbacks: { label: (ctx) => ` ${ctx.raw > 0 ? '+' : ''}${ctx.raw}pp` },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: { autoSkip: false, color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } },
      },
      y: {
        grid: { color: palette.border },
        border: { display: false },
        ticks: {
          callback: (v) => `${v > 0 ? '+' : ''}${v}pp`,
          color: palette.slateDeep,
          font: { family: 'JetBrains Mono', size: 10, weight: '500' },
        },
      },
    },
  };
}

/* ──────────────────────────── MFL SECTION ──────────────────────────── */

function MFLSection({ rows }) {
  const mflData = MISC_CM_MFL;
  const pmTrend = mflData.pmTrend;

  return (
    <div className="space-y-6">
      {/* Domain header */}
      <div className="domain-section-hdr" style={{ background: DOMAIN_GRADIENTS.MFL }}>
        <h2>MFL - Main Feeder Links</h2>
        <span className="domain-section-chip">{rows.length} CMPs</span>
        <span className="domain-section-chip">{rows.reduce((s, r) => s + r.sites, 0)} Sites</span>
        <span className="domain-section-chip">100% Availability</span>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {mflData.kpis.map((kpi) => (
          <KpiCard
            key={kpi.label}
            label={kpi.label}
            value={kpi.value}
            sub={kpi.sub}
            dot={toneColor(kpi.tone)}
            valueColor={toneColor(kpi.tone)}
          />
        ))}
      </div>

      {/* Charts Row: KPI Score + Energy Compliance */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="card-surface p-6">
          <SectionTitle dot="#3b82f6">MFL KPI Score % by CMP</SectionTitle>
          <div style={{ height: 252 }}>
            <Bar
              data={{
                labels: rows.map((r) => r.cmp),
                datasets: [{
                  data: rows.map((r) => r.kpi_pct),
                  backgroundColor: rows.map((r) => kpiPctColor(r.kpi_pct)),
                  borderRadius: 4,
                }],
              }}
              options={barOpts(50, 100)}
            />
          </div>
        </div>
        <div className="card-surface p-6">
          <SectionTitle dot={palette.warning}>MFL Energy Compliance % by CMP</SectionTitle>
          <div style={{ height: 252 }}>
            <Bar
              data={{
                labels: rows.map((r) => r.cmp),
                datasets: [{
                  data: rows.map((r) => r.energy_pct),
                  backgroundColor: rows.map((r) => energyBarColor(r.energy_pct)),
                  borderRadius: 4,
                }],
              }}
              options={barOpts(0, 100)}
            />
          </div>
        </div>
      </div>

      {/* Visibility Chart */}
      <div className="card-surface p-6">
        <SectionTitle dot={palette.accent}>MFL Visibility % by CMP</SectionTitle>
        <div style={{ height: 252 }}>
          <Bar
            data={{
              labels: rows.map((r) => r.cmp),
              datasets: [{
                data: rows.map((r) => r.vis_pct),
                backgroundColor: rows.map((r) => visBarColor(r.vis_pct)),
                borderRadius: 4,
              }],
            }}
            options={barOpts(0, 100)}
          />
        </div>
      </div>

      {/* Performance Table */}
      <div className="card-surface p-6">
        <SectionTitle dot="#3b82f6">MFL CMP Performance Table</SectionTitle>
        <div className="mt-4 table-scroll-shell">
          <table className="evidence-table">
            <thead>
              <tr>
                <th>CMP</th>
                <th>Circle</th>
                <th>Sites</th>
                <th>PM Feb</th>
                <th>PM Jan</th>
                <th>Consec Miss</th>
                <th>KPI %</th>
                <th>Energy %</th>
                <th>Avail %</th>
                <th>Visibility %</th>
                <th>Penalty Sites</th>
                <th>OK Sites</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.cmp}>
                  <td className="font-semibold text-slate-700">{r.cmp}</td>
                  <td>
                    <span
                      className="inline-flex rounded-full px-2 py-1 text-[10px] font-semibold"
                      style={{ backgroundColor: `${MISC_CIRCLE_COLORS[r.circle]}16`, color: MISC_CIRCLE_COLORS[r.circle] }}
                    >
                      {r.circle}
                    </span>
                  </td>
                  <td className="mono-cell">{r.sites}</td>
                  <td className="mono-cell">{r.pm_curr}</td>
                  <td className="mono-cell">{r.pm_last}</td>
                  <td className="mono-cell">
                    {r.consec2 > 0 ? (
                      <span className="inline-flex rounded-md bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700">{r.consec2}</span>
                    ) : '0'}
                  </td>
                  <td className="mono-cell">
                    <span className="inline-flex min-w-[2.25rem] items-center justify-center rounded-md px-1.5 py-1 text-[11px] font-semibold" style={pctCellStyle(r.kpi_pct, 90)}>
                      {r.kpi_pct}%
                    </span>
                  </td>
                  <td className="mono-cell">
                    <span className="inline-flex min-w-[2.25rem] items-center justify-center rounded-md px-1.5 py-1 text-[11px] font-semibold" style={pctCellStyle(r.energy_pct, 70)}>
                      {r.energy_pct}%
                    </span>
                  </td>
                  <td className="mono-cell" style={{ color: r.avail_pct >= 99.95 ? palette.success : palette.warning }}>
                    {r.avail_pct}%
                  </td>
                  <td className="mono-cell">
                    <span className="inline-flex min-w-[2.25rem] items-center justify-center rounded-md px-1.5 py-1 text-[11px] font-semibold" style={pctCellStyle(r.vis_pct, 70)}>
                      {r.vis_pct}%
                    </span>
                  </td>
                  <td className="mono-cell font-semibold text-red-500">{r.pen_sites}</td>
                  <td className="mono-cell font-semibold text-emerald-600">{r.ok_sites}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* PM Compliance Trend Sub-Section */}
      <div className="overflow-hidden rounded-2xl border border-blue-200">
        <div className="bg-blue-50 px-5 py-3">
          <span className="text-[13px] font-semibold text-blue-800">MFL PM Compliance Trend - Jan'26 vs Feb'26</span>
        </div>
        <div className="space-y-5 p-5">
          {/* PM Trend KPIs */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {pmTrend.kpis.map((kpi) => (
              <KpiCard
                key={kpi.label}
                label={kpi.label}
                value={kpi.value}
                sub={kpi.sub}
                dot={toneColor(kpi.tone)}
                valueColor={toneColor(kpi.tone)}
              />
            ))}
          </div>

          {/* PM Trend Charts: Jan vs Feb + Delta */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="card-surface p-6">
              <SectionTitle dot="#3b82f6">PM TECO % - Jan'26 vs Feb'26</SectionTitle>
              <div style={{ height: 252 }}>
                <Bar
                  data={{
                    labels: pmTrend.charts.trend.labels,
                    datasets: [
                      {
                        label: "Jan'26",
                        data: pmTrend.charts.trend.jan,
                        backgroundColor: palette.slate,
                        borderRadius: 4,
                      },
                      {
                        label: "Feb'26",
                        data: pmTrend.charts.trend.feb,
                        backgroundColor: palette.accent,
                        borderRadius: 4,
                      },
                    ],
                  }}
                  options={groupedBarOpts(50, 105)}
                />
              </div>
            </div>
            <div className="card-surface p-6">
              <SectionTitle dot={palette.success}>Month-on-Month Delta (pp)</SectionTitle>
              <div style={{ height: 252 }}>
                <Bar
                  data={{
                    labels: pmTrend.charts.delta.labels,
                    datasets: [{
                      data: pmTrend.charts.delta.data,
                      backgroundColor: pmTrend.charts.delta.data.map((v) => v >= 0 ? palette.success : palette.danger),
                      borderRadius: 4,
                    }],
                  }}
                  options={deltaBarOpts()}
                />
              </div>
            </div>
          </div>

          {/* PM Trend Comparison Table */}
          <div className="card-surface p-6">
            <SectionTitle dot={palette.navy}>MFL PM Comparison by CMP</SectionTitle>
            <div className="mt-4 table-scroll-shell">
              <table className="evidence-table">
                <thead>
                  <tr>
                    <th>CMP</th>
                    <th>Circle</th>
                    <th>Jan'26 Done</th>
                    <th>Jan %</th>
                    <th>Feb'26 TECO</th>
                    <th>Feb %</th>
                    <th>Delta</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {pmTrend.table.map((r) => (
                    <tr key={r.cmp}>
                      <td className="font-semibold text-slate-700">{r.cmp}</td>
                      <td>
                        <span
                          className="inline-flex rounded-full px-2 py-1 text-[10px] font-semibold"
                          style={{ backgroundColor: `${MISC_CIRCLE_COLORS[r.circle]}16`, color: MISC_CIRCLE_COLORS[r.circle] }}
                        >
                          {r.circle}
                        </span>
                      </td>
                      <td className="mono-cell">{r.janDone}</td>
                      <td className="mono-cell">
                        <span className="inline-flex min-w-[2.25rem] items-center justify-center rounded-md px-1.5 py-1 text-[11px] font-semibold" style={pctCellStyle(r.janPct, 95)}>
                          {r.janPct}%
                        </span>
                      </td>
                      <td className="mono-cell">{r.febTeco}</td>
                      <td className="mono-cell">
                        <span className="inline-flex min-w-[2.25rem] items-center justify-center rounded-md px-1.5 py-1 text-[11px] font-semibold" style={pctCellStyle(r.febPct, 95)}>
                          {r.febPct}%
                        </span>
                      </td>
                      <td className="mono-cell" style={{ color: r.delta.startsWith('+') ? palette.success : r.delta.startsWith('-') ? palette.danger : palette.slateDeep }}>
                        {r.delta}
                      </td>
                      <td className="text-[11px] text-slate-500">{r.febNote ?? '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────── AG1 SECTION ──────────────────────────── */

function AG1Section({ rows }) {
  const ag1Data = MISC_CM_AG1;
  const pmTrend = ag1Data.pmTrend;

  return (
    <div className="space-y-6">
      {/* Domain header */}
      <div className="domain-section-hdr" style={{ background: DOMAIN_GRADIENTS.AG1 }}>
        <h2>AG1 - Aggregator Nodes Level 1</h2>
        <span className="domain-section-chip">{rows.length} CMPs</span>
        <span className="domain-section-chip">{rows.reduce((s, r) => s + r.sites, 0)} Sites</span>
        <span className="domain-section-chip domain-section-chip-warn">All in 7.5% Penalty</span>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {ag1Data.kpis.map((kpi) => (
          <KpiCard
            key={kpi.label}
            label={kpi.label}
            value={kpi.value}
            sub={kpi.sub}
            dot={toneColor(kpi.tone)}
            valueColor={toneColor(kpi.tone)}
          />
        ))}
      </div>

      {/* Charts: KPI Score + Energy */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="card-surface p-6">
          <SectionTitle dot="#ec4899">AG1 KPI Score % by CMP</SectionTitle>
          <div style={{ height: 252 }}>
            <Bar
              data={{
                labels: rows.map((r) => r.cmp),
                datasets: [{
                  data: rows.map((r) => r.kpi_pct),
                  backgroundColor: rows.map((r) => kpiPctColor(r.kpi_pct)),
                  borderRadius: 4,
                }],
              }}
              options={barOpts(40, 100)}
            />
          </div>
        </div>
        <div className="card-surface p-6">
          <SectionTitle dot={palette.warning}>AG1 Energy Compliance % by CMP</SectionTitle>
          <div style={{ height: 252 }}>
            <Bar
              data={{
                labels: rows.map((r) => r.cmp),
                datasets: [{
                  data: rows.map((r) => r.energy_pct),
                  backgroundColor: rows.map((r) => ag1EnergyBarColor(r.energy_pct)),
                  borderRadius: 4,
                }],
              }}
              options={barOpts(0, 100)}
            />
          </div>
        </div>
      </div>

      {/* Visibility Chart */}
      <div className="card-surface p-6">
        <SectionTitle dot={palette.accent}>AG1 Visibility % by CMP</SectionTitle>
        <div style={{ height: 252 }}>
          <Bar
            data={{
              labels: rows.map((r) => r.cmp),
              datasets: [{
                data: rows.map((r) => r.vis_pct),
                backgroundColor: rows.map((r) => visBarColor(r.vis_pct)),
                borderRadius: 4,
              }],
            }}
            options={barOpts(0, 100)}
          />
        </div>
      </div>

      {/* Performance Table */}
      <div className="card-surface p-6">
        <SectionTitle dot="#ec4899">AG1 CMP Performance Table</SectionTitle>
        <div className="mt-4 table-scroll-shell">
          <table className="evidence-table">
            <thead>
              <tr>
                <th>CMP</th>
                <th>Circle</th>
                <th>Sites</th>
                <th>PM Feb</th>
                <th>PM Jan</th>
                <th>Consec Miss</th>
                <th>KPI %</th>
                <th>Energy %</th>
                <th>Avail %</th>
                <th>Visibility %</th>
                <th>Penalty Sites</th>
                <th>OK Sites</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.cmp}>
                  <td className="font-semibold text-slate-700">{r.cmp}</td>
                  <td>
                    <span
                      className="inline-flex rounded-full px-2 py-1 text-[10px] font-semibold"
                      style={{ backgroundColor: `${MISC_CIRCLE_COLORS[r.circle]}16`, color: MISC_CIRCLE_COLORS[r.circle] }}
                    >
                      {r.circle}
                    </span>
                  </td>
                  <td className="mono-cell">{r.sites}</td>
                  <td className="mono-cell">{r.pm_curr}</td>
                  <td className="mono-cell">{r.pm_last}</td>
                  <td className="mono-cell">
                    {r.consec2 > 0 ? (
                      <span className="inline-flex rounded-md bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700">{r.consec2}</span>
                    ) : '0'}
                  </td>
                  <td className="mono-cell">
                    <span className="inline-flex min-w-[2.25rem] items-center justify-center rounded-md px-1.5 py-1 text-[11px] font-semibold" style={pctCellStyle(r.kpi_pct, 90)}>
                      {r.kpi_pct}%
                    </span>
                  </td>
                  <td className="mono-cell">
                    <span className="inline-flex min-w-[2.25rem] items-center justify-center rounded-md px-1.5 py-1 text-[11px] font-semibold" style={pctCellStyle(r.energy_pct, 70)}>
                      {r.energy_pct}%
                    </span>
                  </td>
                  <td className="mono-cell" style={{ color: r.avail_pct >= 99.95 ? palette.success : palette.warning }}>
                    {r.avail_pct}%
                  </td>
                  <td className="mono-cell">
                    <span className="inline-flex min-w-[2.25rem] items-center justify-center rounded-md px-1.5 py-1 text-[11px] font-semibold" style={pctCellStyle(r.vis_pct, 50)}>
                      {r.vis_pct}%
                    </span>
                  </td>
                  <td className="mono-cell font-semibold text-red-500">{r.pen_sites}</td>
                  <td className="mono-cell font-semibold text-emerald-600">{r.ok_sites}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* PM Compliance Trend Sub-Section */}
      <div className="overflow-hidden rounded-2xl border border-pink-200">
        <div className="bg-pink-50 px-5 py-3">
          <span className="text-[13px] font-semibold text-pink-800">AG1 PM Compliance Trend - Jan'26 vs Feb'26</span>
        </div>
        <div className="space-y-5 p-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {pmTrend.kpis.map((kpi) => (
              <KpiCard
                key={kpi.label}
                label={kpi.label}
                value={kpi.value}
                sub={kpi.sub}
                dot={toneColor(kpi.tone)}
                valueColor={toneColor(kpi.tone)}
              />
            ))}
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="card-surface p-6">
              <SectionTitle dot="#ec4899">PM TECO % - Jan'26 vs Feb'26</SectionTitle>
              <div style={{ height: 252 }}>
                <Bar
                  data={{
                    labels: pmTrend.charts.trend.labels,
                    datasets: [
                      {
                        label: "Jan'26",
                        data: pmTrend.charts.trend.jan,
                        backgroundColor: palette.slate,
                        borderRadius: 4,
                      },
                      {
                        label: "Feb'26",
                        data: pmTrend.charts.trend.feb,
                        backgroundColor: '#ec4899',
                        borderRadius: 4,
                      },
                    ],
                  }}
                  options={groupedBarOpts(40, 105)}
                />
              </div>
            </div>
            <div className="card-surface p-6">
              <SectionTitle dot={palette.success}>Month-on-Month Delta (pp)</SectionTitle>
              <div style={{ height: 252 }}>
                <Bar
                  data={{
                    labels: pmTrend.charts.delta.labels,
                    datasets: [{
                      data: pmTrend.charts.delta.data,
                      backgroundColor: pmTrend.charts.delta.data.map((v) => v >= 0 ? palette.success : palette.danger),
                      borderRadius: 4,
                    }],
                  }}
                  options={deltaBarOpts()}
                />
              </div>
            </div>
          </div>

          <div className="card-surface p-6">
            <SectionTitle dot={palette.navy}>AG1 PM Comparison by CMP</SectionTitle>
            <div className="mt-4 table-scroll-shell">
              <table className="evidence-table">
                <thead>
                  <tr>
                    <th>CMP</th>
                    <th>Circle</th>
                    <th>Jan'26 Done</th>
                    <th>Jan %</th>
                    <th>Feb'26 Done</th>
                    <th>Feb %</th>
                    <th>Delta</th>
                  </tr>
                </thead>
                <tbody>
                  {pmTrend.table.map((r) => (
                    <tr key={r.cmp}>
                      <td className="font-semibold text-slate-700">{r.cmp}</td>
                      <td>
                        <span
                          className="inline-flex rounded-full px-2 py-1 text-[10px] font-semibold"
                          style={{ backgroundColor: `${MISC_CIRCLE_COLORS[r.circle]}16`, color: MISC_CIRCLE_COLORS[r.circle] }}
                        >
                          {r.circle}
                        </span>
                      </td>
                      <td className="mono-cell">{r.janDone}</td>
                      <td className="mono-cell">
                        <span className="inline-flex min-w-[2.25rem] items-center justify-center rounded-md px-1.5 py-1 text-[11px] font-semibold" style={pctCellStyle(r.janPct, 95)}>
                          {r.janPct}%
                        </span>
                      </td>
                      <td className="mono-cell">{r.febDone}</td>
                      <td className="mono-cell">
                        <span className="inline-flex min-w-[2.25rem] items-center justify-center rounded-md px-1.5 py-1 text-[11px] font-semibold" style={pctCellStyle(r.febPct, 95)}>
                          {r.febPct}%
                        </span>
                      </td>
                      <td className="mono-cell" style={{ color: r.delta.startsWith('+') ? palette.success : r.delta.startsWith('-') ? palette.danger : palette.slateDeep }}>
                        {r.delta}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────── OSC SECTION ──────────────────────────── */

function OSCSection({ rows }) {
  const oscData = MISC_CM_OSC;
  const pmSnap = oscData.pmSnapshot;

  return (
    <div className="space-y-6">
      {/* Domain header */}
      <div className="domain-section-hdr" style={{ background: DOMAIN_GRADIENTS.OSC }}>
        <h2>OSC - Outdoor Small Cells</h2>
        <span className="domain-section-chip">{rows.length} CMPs</span>
        <span className="domain-section-chip">{rows.reduce((s, r) => s + r.sites, 0).toLocaleString('en-IN')} Sites</span>
        <span className="domain-section-chip">{rows.reduce((s, r) => s + r.cells, 0).toLocaleString('en-IN')} Cells</span>
        <span className="domain-section-chip domain-section-chip-warn">Avg Avail 93.7%</span>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {oscData.kpis.map((kpi) => (
          <KpiCard
            key={kpi.label}
            label={kpi.label}
            value={kpi.value}
            sub={kpi.sub}
            dot={toneColor(kpi.tone)}
            valueColor={toneColor(kpi.tone)}
          />
        ))}
      </div>

      {/* Charts Row 1: Availability + SA Alarm */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="card-surface p-6">
          <SectionTitle dot={palette.danger}>OSC Availability % by CMP</SectionTitle>
          <p className="dashboard-note">Target: {'>'}99.95% - zone avg 93.7%</p>
          <div style={{ height: 252 }}>
            <Bar
              data={{
                labels: rows.map((r) => r.cmp),
                datasets: [{
                  data: rows.map((r) => r.avail),
                  backgroundColor: rows.map((r) => availBarColor(r.avail)),
                  borderRadius: 4,
                }],
              }}
              options={barOpts(70, 100)}
            />
          </div>
        </div>
        <div className="card-surface p-6">
          <SectionTitle dot={palette.orange}>OSC SA Alarm Closure % by CMP</SectionTitle>
          <p className="dashboard-note">Target: 100% - zone avg 42%</p>
          <div style={{ height: 252 }}>
            <Bar
              data={{
                labels: rows.map((r) => r.cmp),
                datasets: [{
                  data: rows.map((r) => r.sa),
                  backgroundColor: rows.map((r) => saBarColor(r.sa)),
                  borderRadius: 4,
                }],
              }}
              options={barOpts(0, 100)}
            />
          </div>
        </div>
      </div>

      {/* Charts Row 2: PM TECO + SMPS */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="card-surface p-6">
          <SectionTitle dot={palette.warning}>OSC PM TECO Rate by CMP</SectionTitle>
          <div style={{ height: 252 }}>
            <Bar
              data={{
                labels: rows.map((r) => r.cmp),
                datasets: [{
                  data: rows.map((r) => r.pm_pct),
                  backgroundColor: rows.map((r) => pmTecoBarColor(r.pm_pct)),
                  borderRadius: 4,
                }],
              }}
              options={barOpts(0, 100)}
            />
          </div>
        </div>
        <div className="card-surface p-6">
          <SectionTitle dot={palette.danger}>SMPS Unreachable Cases by CMP</SectionTitle>
          <div style={{ height: 252 }}>
            <Bar
              data={{
                labels: rows.map((r) => r.cmp),
                datasets: [{
                  data: rows.map((r) => r.smps),
                  backgroundColor: rows.map((r) => MISC_CIRCLE_COLORS[r.circle] ?? palette.slate),
                  borderRadius: 4,
                }],
              }}
              options={barOpts(0, undefined, false)}
            />
          </div>
        </div>
      </div>

      {/* Performance Table */}
      <div className="card-surface p-6">
        <SectionTitle dot="#059669">OSC CMP Performance Table</SectionTitle>
        <div className="mt-4 table-scroll-shell">
          <table className="evidence-table">
            <thead>
              <tr>
                <th>CMP</th>
                <th>Circle</th>
                <th>Sites</th>
                <th>Cells</th>
                <th>PM TECO</th>
                <th>PM %</th>
                <th>Avail %</th>
                <th>SA %</th>
                <th>UBR RSL</th>
                <th>SMPS</th>
                <th>UBR Port</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.cmp}>
                  <td className="font-semibold text-slate-700">{r.cmp}</td>
                  <td>
                    <span
                      className="inline-flex rounded-full px-2 py-1 text-[10px] font-semibold"
                      style={{ backgroundColor: `${MISC_CIRCLE_COLORS[r.circle]}16`, color: MISC_CIRCLE_COLORS[r.circle] }}
                    >
                      {r.circle}
                    </span>
                  </td>
                  <td className="mono-cell">{r.sites}</td>
                  <td className="mono-cell">{r.cells}</td>
                  <td className="mono-cell">{r.pm_teco}</td>
                  <td className="mono-cell">
                    <span className="inline-flex min-w-[2.25rem] items-center justify-center rounded-md px-1.5 py-1 text-[11px] font-semibold" style={pctCellStyle(r.pm_pct, 70)}>
                      {r.pm_pct}%
                    </span>
                  </td>
                  <td className="mono-cell">
                    <span className="inline-flex min-w-[2.25rem] items-center justify-center rounded-md px-1.5 py-1 text-[11px] font-semibold" style={pctCellStyle(r.avail, 99.95)}>
                      {r.avail}%
                    </span>
                  </td>
                  <td className="mono-cell">
                    <span className="inline-flex min-w-[2.25rem] items-center justify-center rounded-md px-1.5 py-1 text-[11px] font-semibold" style={pctCellStyle(r.sa, 80)}>
                      {r.sa}%
                    </span>
                  </td>
                  <td className="mono-cell">{r.ubr_rsl}</td>
                  <td className="mono-cell font-semibold" style={{ color: r.smps > 150 ? palette.danger : r.smps > 100 ? palette.warning : palette.slateDeep }}>
                    {r.smps}
                  </td>
                  <td className="mono-cell">{r.ubr_port}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* OSC PM Snapshot Sub-Section */}
      <div className="overflow-hidden rounded-2xl border border-emerald-200">
        <div className="bg-emerald-50 px-5 py-3">
          <span className="text-[13px] font-semibold text-emerald-800">OSC PM Compliance Snapshot - Feb'26</span>
        </div>
        <div className="space-y-5 p-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {pmSnap.kpis.map((kpi) => (
              <KpiCard
                key={kpi.label}
                label={kpi.label}
                value={kpi.value}
                sub={kpi.sub}
                dot={toneColor(kpi.tone)}
                valueColor={toneColor(kpi.tone)}
              />
            ))}
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="card-surface p-6">
              <SectionTitle dot="#059669">OSC PM TECO % by CMP - Feb'26</SectionTitle>
              <div style={{ height: 252 }}>
                <Bar
                  data={{
                    labels: pmSnap.charts.trend.labels,
                    datasets: [{
                      data: pmSnap.charts.trend.feb,
                      backgroundColor: pmSnap.charts.trend.colors,
                      borderColor: pmSnap.charts.trend.borders,
                      borderWidth: 1,
                      borderRadius: 4,
                    }],
                  }}
                  options={barOpts(0, 100)}
                />
              </div>
            </div>
            <div className="card-surface p-6">
              <SectionTitle dot={palette.accent}>OSC PM TECO % by Circle</SectionTitle>
              <div style={{ height: 252 }}>
                <Bar
                  data={{
                    labels: pmSnap.charts.circle.labels,
                    datasets: [{
                      data: pmSnap.charts.circle.data,
                      backgroundColor: pmSnap.charts.circle.colors,
                      borderColor: pmSnap.charts.circle.borders,
                      borderWidth: 1,
                      borderRadius: 4,
                    }],
                  }}
                  options={barOpts(0, 100)}
                />
              </div>
            </div>
          </div>

          <div className="card-surface p-6">
            <SectionTitle dot={palette.navy}>OSC PM Completion by CMP</SectionTitle>
            <div className="mt-4 table-scroll-shell">
              <table className="evidence-table">
                <thead>
                  <tr>
                    <th>CMP</th>
                    <th>Circle</th>
                    <th>Done / Scope</th>
                    <th>TECO %</th>
                    <th>Outstanding</th>
                  </tr>
                </thead>
                <tbody>
                  {pmSnap.table.map((r) => (
                    <tr key={r.cmp}>
                      <td className="font-semibold text-slate-700">{r.cmp}</td>
                      <td>
                        <span
                          className="inline-flex rounded-full px-2 py-1 text-[10px] font-semibold"
                          style={{ backgroundColor: `${MISC_CIRCLE_COLORS[r.circle]}16`, color: MISC_CIRCLE_COLORS[r.circle] }}
                        >
                          {r.circle}
                        </span>
                      </td>
                      <td className="mono-cell">{r.doneScope}</td>
                      <td className="mono-cell">
                        <span className="inline-flex min-w-[2.25rem] items-center justify-center rounded-md px-1.5 py-1 text-[11px] font-semibold" style={pctCellStyle(r.tecoPct, 70)}>
                          {r.tecoPct}%
                        </span>
                      </td>
                      <td className="mono-cell font-semibold" style={{ color: r.outstanding > 300 ? palette.danger : r.outstanding > 150 ? palette.warning : palette.slateDeep }}>
                        {r.outstanding}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────── MAIN COMPONENT ──────────────────────────── */

export default function MiscCM({ selectedMonth }) {
  const [activeCircle, setActiveCircle] = useState('ALL');
  const [activeDomain, setActiveDomain] = useState('MFL');

  // Filter table rows by circle
  const filterByCircle = (rows) =>
    activeCircle === 'ALL' ? rows : rows.filter((r) => r.circle === activeCircle);

  const mflRows = filterByCircle(MISC_CM_MFL.table);
  const ag1Rows = filterByCircle(MISC_CM_AG1.table);
  const oscRows = filterByCircle(MISC_CM_OSC.table);

  if (selectedMonth !== "Feb'26") {
    return <PeriodUnavailableState monthLabel={selectedMonth} />;
  }

  return (
    <div className="space-y-6">
      {/* 1. Filter Strip */}
      <ChipStrip groups={[
        { label: 'Circle', value: activeCircle, onChange: setActiveCircle, options: CIRCLE_OPTIONS },
        { label: 'Type', value: activeDomain, onChange: setActiveDomain, options: DOMAIN_TABS.map((t) => ({ value: t.key, label: t.label })) },
      ]} />

      {/* 5/6/7. Domain-Specific Content */}
      {activeDomain === 'MFL' && <MFLSection rows={mflRows} />}
      {activeDomain === 'AG1' && <AG1Section rows={ag1Rows} />}
      {activeDomain === 'OSC' && <OSCSection rows={oscRows} />}

      {/* Empty state for filtered views */}
      {activeDomain === 'MFL' && mflRows.length === 0 && <EmptyState />}
      {activeDomain === 'AG1' && ag1Rows.length === 0 && <EmptyState />}
      {activeDomain === 'OSC' && oscRows.length === 0 && <EmptyState />}

      {/* 8. Executive Insights */}
      <InsightBanner chips={MISC_CM_INSIGHT_CHIPS} />

      <div className="grid grid-cols-1 gap-4">
        {MISC_CM_INSIGHTS.map((insight) => (
          <InsightCard
            key={insight.title}
            tone={insight.tone}
            title={insight.title}
            exposure={insight.exposure}
            body={insight.body}
            actionTitle={insight.actionTitle}
            action={insight.action}
          >
            {insight.metrics?.length ? (
              <div className="mt-3 flex flex-wrap gap-3">
                {insight.metrics.map((m) => {
                  const accent = toneAccent(m.tone);
                  return (
                    <div key={m.label} className="rounded-lg px-3 py-2 text-center" style={{ backgroundColor: accent.soft }}>
                      <div className="text-[14px] font-bold" style={{ color: accent.line }}>{m.value}</div>
                      <div className="text-[10px] font-medium text-slate-500">{m.label}</div>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </InsightCard>
        ))}
      </div>

      {/* 9. Action Plan */}
      <div className="card-surface border border-emerald-200 bg-emerald-50/80 p-6">
        <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-emerald-700">Recoverable Upside</div>
        <div className="mt-2 text-[13px] font-semibold leading-6 text-emerald-900">{MISC_CM_RECOVERABLE_BANNER}</div>
      </div>

      <div className="card-surface p-6">
        <SectionTitle dot={palette.success}>Prioritised Action Plan</SectionTitle>
        <div className="table-scroll-shell">
          <table className="action-table">
            <thead>
              <tr>
                <th>Priority</th>
                <th>Domain</th>
                <th>Action</th>
                <th>CMPs Impacted</th>
                <th>Owner</th>
                <th>Expected Impact</th>
              </tr>
            </thead>
            <tbody>
              {MISC_CM_ACTIONS.map((row) => (
                <tr key={row.action}>
                  <td><PriorityBadge priority={row.priority} /></td>
                  <td>
                    <span className="text-[11px] font-semibold text-slate-600">{row.domain}</span>
                  </td>
                  <td>
                    <div className="font-semibold text-slate-700">{row.action}</div>
                    <div className="mt-1 text-[11px] leading-5 text-slate-500">{row.detail}</div>
                  </td>
                  <td className="text-slate-600">{row.cmps}</td>
                  <td className="text-slate-600">{row.owner}</td>
                  <td className="text-[11px] leading-5 text-slate-600">{row.impact}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
